/**
 * This file contains the shared logic for the order list page.
 * It is used to get the status filter options for the current platform.
 * It is also used to get the statuses from a filter value.
 * It is also used to parse the status filter value.
 */
import { SAMD_STATUS_FILTER_OPTIONS } from '@samd/features/orders/statuses';
import { getLdtWorkflowDefinition } from '@ldt/workflows/registry';
import { getPlatform } from 'src/config/environment';
import {
  ALL_ORDERS_FILTER,
  ALL_SPECIMEN_TYPE_FILTER,
} from 'src/constants/key_constants';
import { SpecimenType } from 'src/types';
import { DistributionPlatform } from 'src/types/env';
import { OrderStatusFilterOption } from 'src/types/status_filter';
import type { WorkflowType } from 'src/types/workflows';

/** Placeholder and width for the order list search field. */
export type OrderListSearchConfig = {
  placeholder: string;
  width: string;
};

/**
 * Returns the status filter options for the current platform.
 * LDT: uses workflow (workflowType) to get status filter options via workflow.getStatusFilterOptions.
 * SaMD: uses SAMD_STATUS_FILTER_OPTIONS.
 */
export const getStatusFilterOptions = (
  workflowType: WorkflowType,
): Map<string, OrderStatusFilterOption> => {
  const platform = getPlatform();
  if (platform === DistributionPlatform.Ldt) {
    const workflow = getLdtWorkflowDefinition(workflowType);
    return workflow.getStatusFilterOptions();
  } else if (platform === DistributionPlatform.Samd) {
    // All SaMD status filter options are the same for all regions.
    return SAMD_STATUS_FILTER_OPTIONS;
  } else {
    throw new Error(`Unsupported platform: ${platform}`);
  }
};

const SPECIMEN_TYPE_FILTER_OPTIONS: Record<string, SpecimenType> = {
  [SpecimenType.BreastBiopsy]: SpecimenType.BreastBiopsy,
  [SpecimenType.ProstateBiopsy]: SpecimenType.ProstateBiopsy,
  [SpecimenType.ProstatePostRp]: SpecimenType.ProstatePostRp,
  [SpecimenType.MetastaticProstateBiopsy]:
    SpecimenType.MetastaticProstateBiopsy,
};

// Helper functions that works for all platforms.
export const getStatusesFromFilter = (
  filterValue: string,
  workflowType: WorkflowType,
) => {
  // "All Orders" with no statuses means unfiltered (undefined → statuses: null).
  // When All Orders defines statuses (e.g. Direct Upload lab tech), those are used.
  const option = getStatusFilterOptions(workflowType).get(filterValue);
  return option?.statuses;
};

export const getLabelFromFilter = (
  filterValue: string,
  workflowType: WorkflowType,
): string => {
  const option = getStatusFilterOptions(workflowType).get(filterValue);
  return option!.label;
};

// All order status filter
export const isAllOrdersFilter = (filterValue: string): boolean => {
  return filterValue === ALL_ORDERS_FILTER;
};

// All order specimen type filter
export const isAllSpecimenTypeFilter = (filterValues: string[]): boolean => {
  return (
    filterValues.length === 0 ||
    (filterValues.length === 1 && filterValues[0] === ALL_SPECIMEN_TYPE_FILTER)
  );
};

export const parseStatusFilterValue = (value: string | null): string => {
  if (!value) return ALL_ORDERS_FILTER;
  return value;
};

export const parseSpecimenTypeFilterValues = (
  value: string | null,
): SpecimenType[] => {
  if (!value) return [];

  const parsedValues = value.split(',');
  try {
    return Array.isArray(parsedValues)
      ? parsedValues
          .map((v: string) => SPECIMEN_TYPE_FILTER_OPTIONS[v])
          .filter((specimenType: SpecimenType) => specimenType !== undefined)
      : [];
  } catch {
    return [];
  }
};
